/**
 * CheckInV2 board data — one load call per view (dashboard + the visible
 * month's events + the staff profile map), refreshed silently by a single
 * debounced realtime subscription. No per-row fetches.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarEvent } from '@/lib/api/calendar-api';
import {
  fetchCheckIns,
  fetchEvents,
  fetchProfilesByEmails,
  subscribeToBoard,
} from './api';
import { ALL_COLUMNS } from './config';
import { formatDateLocal } from './slots';
import type { AppointmentEvent, CheckInRow, Profile } from './types';

const REALTIME_DEBOUNCE_MS = 800;

export interface BoardData {
  checkIns: CheckInRow[];
  events: CalendarEvent[];
  profiles: Profile[];
  emailToProfileId: Record<string, string>;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useBoardData(selectedDate: Date): BoardData {
  const [checkIns, setCheckIns] = useState<CheckInRow[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Month window of the selected date; refetch only when the month changes
  const monthKey = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}`;
  const monthRef = useRef(monthKey);
  monthRef.current = monthKey;

  // Latest-wins: month switches and silent reloads can resolve out of order —
  // only the most recently started load may touch state
  const loadIdRef = useRef(0);

  const load = useCallback(async (silent = false) => {
    const loadId = ++loadIdRef.current;
    if (!silent) setIsLoading(true);
    try {
      const [year, month] = monthRef.current.split('-').map(Number);
      const start = formatDateLocal(new Date(year, month, 1));
      const end = formatDateLocal(new Date(year, month + 1, 0));
      const [checkInRows, eventRows] = await Promise.all([
        fetchCheckIns(),
        fetchEvents(start, end),
      ]);
      if (loadId !== loadIdRef.current) return;
      setCheckIns(checkInRows);
      setEvents(eventRows);
      setError(null);
    } catch (e) {
      if (loadId !== loadIdRef.current) return;
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Failed to load board');
      } else {
        // Keep the previous data on a failed silent refresh, but say so
        console.error('Silent board refresh failed:', e);
      }
    } finally {
      if (loadId === loadIdRef.current) setIsLoading(false);
    }
  }, []);

  // Staff profile map (roster emails → profiles UUIDs) — loaded once
  useEffect(() => {
    const emails = ALL_COLUMNS.filter(s => s.email).map(s => s.email!);
    fetchProfilesByEmails(emails)
      .then(setProfiles)
      .catch(() => setProfiles([]));
  }, []);

  useEffect(() => {
    void load();
  }, [load, monthKey]);

  // One realtime channel, debounced into silent reloads
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeToBoard(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(true), REALTIME_DEBOUNCE_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [load]);

  const emailToProfileId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of profiles) {
      if (p.email) map[p.email.toLowerCase()] = p.id;
    }
    return map;
  }, [profiles]);

  return {
    checkIns,
    events,
    profiles,
    emailToProfileId,
    isLoading,
    error,
    reload: () => load(true),
  };
}

/** Events on one date, each enriched with its matching check-in */
export function useDayAppointments(
  events: CalendarEvent[],
  checkIns: CheckInRow[],
  selectedDate: Date
): AppointmentEvent[] {
  return useMemo(() => {
    const dateStr = formatDateLocal(selectedDate);
    const byEventId = new Map<string, CheckInRow>();
    for (const c of checkIns) {
      if (c.link?.calendar_event_id) {
        byEventId.set(c.link.calendar_event_id, c);
      }
    }
    return events
      .filter(e => e.event_date === dateStr)
      .map(e => ({ ...e, checkIn: byEventId.get(e.google_event_id) ?? null }));
  }, [events, checkIns, selectedDate]);
}

/** Check-ins on one date that have no calendar event (walk-ins, drop-offs) */
export function useUnscheduledCheckIns(
  checkIns: CheckInRow[],
  selectedDate: Date
): CheckInRow[] {
  return useMemo(() => {
    const dateStr = formatDateLocal(selectedDate);
    return checkIns
      .filter(c => {
        if (c.link?.calendar_event_id) return false;
        // appointment_date is a local wall-clock string, but created_at is a
        // UTC timestamp — convert it to the local day before comparing
        const anchor = c.link?.appointment_date
          ? c.link.appointment_date.slice(0, 10)
          : formatDateLocal(new Date(c.created_at));
        return anchor === dateStr;
      })
      .sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  }, [checkIns, selectedDate]);
}
